// vue
import { ref } from '@vue/composition-api';

// services
import { useStore } from '@/store';

// hooks
import { useAxios } from '@/vue3/hooks/axiosHook';

// models
import {
    GeoRecord,
    GeoRecordArchivesEvent,
    GeoRecordArchiveTreeRole,
} from '@/vue3/api/models';

export const useGeoRecordEventService = () => {
    // hooks
    const { apiUrl } = useStore().state;
    const axios = useAxios();

    // reactive
    const geoRecordArchiveEvents = ref<GeoRecordArchivesEvent[]>([]);

    // methods
    const createGeoRecordArchiveEvent = async (
        event: GeoRecordArchivesEvent,
    ): Promise<GeoRecordArchivesEvent[]> => {
        const res = await axios.post(`${apiUrl}/archive/event`, event);
        return res.data;
    };

    const getAllArchiveEvents = async (): Promise<GeoRecordArchivesEvent[]> => {
        const res = await axios.get(`${apiUrl}/archive/events`);
        geoRecordArchiveEvents.value = res.data;
        return res.data;
    };

    const getAllEventsByCourseSchemaIdAndOrgId = async (
        courseSchemaId: number,
    ): Promise<GeoRecordArchivesEvent[]> => {
        const res = await axios.get(
            `${apiUrl}/archive/course-schema/events/${courseSchemaId}`,
        );
        geoRecordArchiveEvents.value = res.data;
        return res.data;
    };

    const getGeoRecordArchiveEvent = async (
        id: number,
    ): Promise<GeoRecordArchivesEvent> => {
        const res = await axios.get(`${apiUrl}/archive/event/${id}`);
        return res.data;
    };

    const getAllArchiveEventsByOrgId = async (
        organizationId: number,
    ): Promise<GeoRecordArchivesEvent[]> => {
        const res = await axios.get(
            `${apiUrl}/archive/events/${organizationId}`,
        );
        geoRecordArchiveEvents.value = res.data;
        return res.data;
    };

    const updateGeoRecordArchiveEvent = async (
        event: GeoRecordArchivesEvent,
    ): Promise<GeoRecordArchivesEvent[]> => {
        const res = await axios.put(`${apiUrl}/archive/event`, event);
        return res.data;
    };

    const deleteGeoRecordEvent = async (id: number) => {
        const res = await axios.deleteId(`${apiUrl}/archive/event/${id}`);
        return res.data;
    };

    const getAllAsArchiveTreeByRole = async (
        GeoRecordArchiveTreeRole: GeoRecordArchiveTreeRole,
    ): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/geo-record-archive/tree`,
            GeoRecordArchiveTreeRole,
        );
        return res.data;
    };

    const createArchiveGeoRecord = async (geoRecordArchive: GeoRecord) => {
        let fd = new FormData();
        fd.append('json', JSON.stringify(geoRecordArchive));

        const res = await axios.post(`${apiUrl}/geo-record/archive`, fd, 10000);
        return res.data;
    };

    const createMultipleArchiveGeoRecord = async (geoRecordArchives: any) => {
        const res = await axios.post(
            `${apiUrl}/geo-record/archive-multiple`,
            geoRecordArchives,
            10000,
        );
        return res.data;
    };

    const deleteMultipleArchiveGeoRecord = async (ids: any) => {
        const res = await axios.post(
            `${apiUrl}/geo-record/archive/delete-multiple`,
            ids,
            10000,
        );
        return res;
    };

    const getAllArchiveGeoRecordByEventId = async (
        courseSchemaId: number,
        archiveEventId: number,
    ): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/geo-record/archive/course-schema/${courseSchemaId}/${archiveEventId}`,
        );
        return res.data;
    };

    const getArchiveGeoRecord = async (
        geoRecordId: number,
    ): Promise<GeoRecord> => {
        const res = await axios.get(
            `${apiUrl}/geo-record/archive/${geoRecordId}/geojson`,
        );
        return res.data;
    };

    const deleteArchiveGeoRecord = async (geoRecordId: number) => {
        const res = await axios.deleteId(
            `${apiUrl}/geo-record/archive/${geoRecordId}/geojson`,
            10000,
        );
        return res;
    };

    const getMultipleArchiveGeoRecords = async (
        geoRecordIds: number[],
    ): Promise<GeoRecord[]> => {
        const res = await axios.post(
            `${apiUrl}/geo-record/archive/geojson/multiple`,
            geoRecordIds,
            10000,
        );
        return res.data;
    };

    return {
        geoRecordArchiveEvents,
        createGeoRecordArchiveEvent,
        getAllEventsByCourseSchemaIdAndOrgId,
        getAllArchiveEvents,
        getGeoRecordArchiveEvent,
        updateGeoRecordArchiveEvent,
        deleteGeoRecordEvent,
        getAllAsArchiveTreeByRole,
        getAllArchiveGeoRecordByEventId,
        getArchiveGeoRecord,
        deleteArchiveGeoRecord,
        createArchiveGeoRecord,
        getAllArchiveEventsByOrgId,
        createMultipleArchiveGeoRecord,
        deleteMultipleArchiveGeoRecord,
        getMultipleArchiveGeoRecords,
    };
};
